import React, { useEffect, useRef, useState, useCallback } from 'react';
import { GameEngine } from '../game/engine';
import { GameNet } from '../game/net';
import { audio } from '../game/audio';

function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => {
    const check = () => setM(('ontouchstart' in window || navigator.maxTouchPoints > 0) && window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return m;
}

function Joystick({ engineRef }) {
  const ref = useRef(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const active = useRef(false);
  const center = useRef({ x: 0, y: 0 });

  const start = (e) => {
    active.current = true;
    const r = ref.current.getBoundingClientRect();
    center.current = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    move(e);
  };
  const move = (e) => {
    if (!active.current) return;
    const t = e.touches ? e.touches[0] : e;
    let dx = t.clientX - center.current.x;
    let dy = t.clientY - center.current.y;
    const max = 46;
    const d = Math.hypot(dx, dy);
    if (d > max) { dx = dx / d * max; dy = dy / d * max; }
    setKnob({ x: dx, y: dy });
    if (engineRef.current) engineRef.current.setJoystick(dx / max, dy / max);
  };
  const end = () => {
    active.current = false;
    setKnob({ x: 0, y: 0 });
    if (engineRef.current) engineRef.current.setJoystick(0, 0);
  };

  return (
    <div ref={ref} className="so-joy" data-testid="mobile-joystick"
      onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}>
      <div className="so-joy-knob" style={{ transform: `translate(${knob.x}px, ${knob.y}px)` }} />
    </div>
  );
}

export default function GameCanvas({ profile, settings, onExit, onCoins, onKeys }) {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const isMobile = useIsMobile();
  const [hud, setHud] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    audio.setSfx(settings.sound);
    audio.musicOn = settings.music;

    const net = new GameNet();
    const engine = new GameEngine(canvas, {
      profile, settings, net, audio, isMobile,
      onHud: (h) => setHud(h),
      onMatchEnd: (r) => { if (onCoins && r.coins) onCoins(r.coins); if (onKeys) onKeys(1); },
    });
    engineRef.current = engine;
    engine.start();

    return () => { window.removeEventListener('resize', resize); engine.stop(); net.close(); };
    // eslint-disable-next-line
  }, []);

  const act = useCallback((name) => { if (engineRef.current) engineRef.current.action(name); }, []);
  const quit = () => {
    if (onCoins && hud && hud.phase === 'gameplay' && hud.coins) onCoins(hud.coins);
    onExit(null);
  };

  const inResults = hud && hud.phase === 'results' && hud.results;

  return (
    <div className="so-game" data-testid="game-screen">
      <canvas ref={canvasRef} className="so-canvas" />

      {hud && (
        <div className="so-hud">
          <div className="so-hud-tl">
            <div className="so-score" data-testid="hud-score">{hud.score.toLocaleString()}<span>pts</span></div>
            <div className="so-substats">
              <span data-testid="hud-snacks">🍿 {hud.snacks}</span>
              <span data-testid="hud-thefts">💥 {hud.thefts}</span>
              <span data-testid="hud-coins">🪙 {hud.coins}</span>
            </div>
          </div>

          <div className="so-hud-tc">
            <div className="so-timer" data-testid="hud-timer">
              {Math.floor(hud.timeLeft / 60)}:{String(hud.timeLeft % 60).padStart(2, '0')}
            </div>
            <div className="so-susp" data-testid="hud-suspicion">
              <div className="so-susp-label" style={{ color: hud.suspColor }}>{hud.suspLabel}</div>
              <div className="so-susp-bar"><div style={{ width: hud.suspicion + '%', background: hud.suspColor }} /></div>
            </div>
          </div>

          <div className="so-hud-tr">
            <div className="so-rank" data-testid="hud-rank">#{hud.rank}<span>/ {hud.total}</span></div>
            <div className="so-players">👥 {hud.players} online</div>
            {hud.phase !== 'results' && <button className="so-quit" onClick={quit} data-testid="quit-match-btn">Quit</button>}
          </div>

          <div className="so-lb" data-testid="hud-leaderboard">
            <div className="so-lb-title">LIVE LEADERBOARD</div>
            {hud.leaderboard.map((p, i) => (
              <div key={p.id} className={'so-lb-row' + (p.me ? ' me' : '')}>
                <span className="so-lb-rank">{i + 1}</span>
                <span className="so-lb-name">{p.name}</span>
                <span className="so-lb-score">{p.score}</span>
              </div>
            ))}
          </div>

          <div className="so-pows">
            {hud.powerups.map((p) => (
              <div key={p.type} className="so-pow" title={p.name}><span>{p.emoji}</span><b>{p.left}s</b></div>
            ))}
          </div>

          {/* combo */}
          {hud.combo >= 2 && hud.phase === 'gameplay' && (
            <div className="so-combo" data-testid="hud-combo">
              <div className="so-combo-txt">🔥 COMBO x{hud.comboMult}</div>
              <div className="so-combo-bar"><div style={{ width: (hud.comboT / 2.5 * 100) + '%' }} /></div>
            </div>
          )}

          {/* boss */}
          {hud.boss && hud.phase === 'gameplay' && (
            <div className="so-boss-badge" data-testid="hud-boss">🕴️ THE MANAGER IS OUT</div>
          )}

          {/* chaos event banner */}
          {hud.event && hud.phase === 'gameplay' && (
            <div className="so-event-banner" data-testid="hud-event" style={{ borderColor: hud.event.color, color: hud.event.color }}>
              <span className="so-event-emoji">{hud.event.emoji}</span>
              <span className="so-event-name">{hud.event.name}</span>
              <span className="so-event-time">{hud.event.timeLeft}s</span>
            </div>
          )}

          {/* abilities: gadget (Q) + throw (F) */}
          {hud.phase === 'gameplay' && !isMobile && (
            <div className="so-abilities" data-testid="abilities-bar">
              {hud.gadget && (
                <button className="so-ability" onClick={() => act('gadget')} disabled={hud.gadget.cd > 0} title={hud.gadget.name} data-testid="ability-gadget">
                  <span>{hud.gadget.icon}</span>
                  <div className="so-ability-cd" style={{ height: (hud.gadget.maxCd ? (hud.gadget.cd / hud.gadget.maxCd * 100) : 0) + '%' }} />
                  <b>Q</b>
                </button>
              )}
              <button className="so-ability throw" onClick={() => act('throw')} disabled={hud.throwCd > 0} title="Throw Snack" data-testid="ability-throw">
                <span>🥤</span>
                <div className="so-ability-cd" style={{ height: (hud.throwCd / 0.75 * 100) + '%' }} />
                <b>F</b>
              </button>
            </div>
          )}

          {hud.phase === 'countdown' && (
            <div className="so-countdown" data-testid="countdown">{hud.countdown > 0 ? hud.countdown : 'GO!'}</div>
          )}
          {hud.phase === 'gameplay' && (
            <div className={'so-stamina' + (hud.exhausted ? ' exhausted' : '')} data-testid="hud-stamina">
              <div className="so-stamina-bar"><div style={{ width: hud.stamina + '%' }} /></div>
              <small>{hud.exhausted ? 'EXHAUSTED — walk to recover' : 'STAMINA'}</small>
            </div>
          )}
          {hud.message && hud.phase === 'gameplay' && (
            <div className="so-message" data-testid="hud-message">{hud.message}</div>
          )}
        </div>
      )}

      {/* synced end-of-match overlay */}
      {inResults && (
        <div className="so-endoverlay" data-testid="ingame-results">
          {hud.results.rank <= 3 && (
            <div className="so-confetti">
              {Array.from({ length: 40 }).map((_, i) => (
                <span key={i} style={{ left: (i * 2.5) + '%', background: ['#ffd23f', '#ff6fb5', '#4d8bff', '#3ddc84', '#b06bff'][i % 5], animationDelay: (i % 10) * 0.15 + 's', animationDuration: (1.6 + (i % 5) * 0.3) + 's' }} />
              ))}
            </div>
          )}
          <div className="so-results so-victory-pop">
            <div className={'so-results-banner' + (hud.results.rank === 1 ? ' win' : '')}>
              {hud.results.rank === 1 ? '🏆 VICTORY!' : 'MATCH OVER'}
            </div>
            <div className="so-results-score">
              <div className="so-results-rank">RANK #{hud.results.rank}<small>of {hud.results.total}</small></div>
              <div className="so-results-pts">{hud.results.score.toLocaleString()}<span>points</span></div>
            </div>
            <div className="so-results-stats">
              <div className="so-rstat"><span className="so-rstat-icon">🍿</span><b>{hud.results.snacks}</b><small>Snacks</small></div>
              <div className="so-rstat"><span className="so-rstat-icon">💥</span><b>{hud.results.thefts}</b><small>Thefts</small></div>
              <div className="so-rstat"><span className="so-rstat-icon">🔥</span><b>x{hud.results.combo}</b><small>Best Combo</small></div>
              <div className="so-rstat"><span className="so-rstat-icon">🚨</span><b>{hud.results.caught}</b><small>Caught</small></div>
              <div className="so-rstat"><span className="so-rstat-icon">🪙</span><b>+{hud.results.coins}</b><small>Coins</small></div>
            </div>
            <div className="so-next">Next match starts in <b>{hud.nextIn}s</b> — stay in for the rematch!</div>
            <button className="so-btn-ghost" onClick={() => onExit(null)} data-testid="ingame-leave">Leave to Menu</button>
          </div>
        </div>
      )}

      {/* chaos event screen tint */}
      {hud && hud.event && hud.phase === 'gameplay' && (
        <div className="so-event-tint" style={{ background: hud.event.tint }} />
      )}

      {isMobile && (
        <>
          <Joystick engineRef={engineRef} />
          <div className="so-touch-btns">
            <button className="so-tb sprint" onTouchStart={() => engineRef.current.setSprint(true)} onTouchEnd={() => engineRef.current.setSprint(false)} data-testid="btn-sprint">🏃</button>
            <button className="so-tb gadget" onTouchStart={() => act('gadget')} data-testid="btn-gadget">{hud && hud.gadget ? hud.gadget.icon : '🚫'}</button>
            <button className="so-tb throwb" onTouchStart={() => act('throw')} data-testid="btn-throw">🥤</button>
            <button className="so-tb interact" onTouchStart={() => act('interact')} data-testid="btn-interact">E</button>
            <button className="so-tb dodge" onTouchStart={() => act('dodge')} data-testid="btn-dodge">↯</button>
            <button className="so-tb kick" onTouchStart={() => act('kick')} data-testid="btn-kick">🦵</button>
            <button className="so-tb punch" onTouchStart={() => act('punch')} data-testid="btn-punch">👊</button>
          </div>
        </>
      )}

      {!isMobile && (
        <div className="so-controls-hint" data-testid="controls-hint">
          <b>WASD</b> move · <b>L</b> punch · <b>R</b> kick · <b>E</b> buy · <b>Q</b> gadget · <b>F</b> throw · <b>Shift</b> sprint · <b>Space</b>/double-tap dash
        </div>
      )}
    </div>
  );
}
